import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useSite } from '../useSite';
import { AUDIENCE_KEYS, SERVICE_KEYS } from '../routes';
import { ArrowLink, ButtonLink, Container, SectionHeading } from './Primitives';
import { Photo } from './Photo';

const AUDIENCE_PHOTO = { investors: 'residence', owners: 'entrepreneur' };

export const AudienceCards = () => {
  const { t, p } = useSite();
  return (
    <div className="grid gap-px border border-tan/50 bg-tan/50 md:grid-cols-2" data-testid="audience-cards">
      {AUDIENCE_KEYS.map((k, i) => (
        <Link key={k} to={p(k)} data-testid={`audience-card-${k}`} className="group relative flex flex-col bg-bone-light transition-colors duration-300 hover:bg-white">
          <div className="aspect-[16/10] overflow-hidden"><Photo name={AUDIENCE_PHOTO[k]} sizes="(min-width: 768px) 50vw, 100vw" className="transition-transform duration-700 group-hover:scale-[1.02]" /></div>
          <div className="flex flex-1 flex-col p-8 md:p-10">
          <span className="font-serif text-sm text-moss-green">0{i + 1}</span>
          <h3 className="mt-6 max-w-sm font-serif text-[1.85rem] leading-tight text-kombu-green">{t.audiences[k].name}</h3>
          <p className="mt-4 flex-1 text-[15px] leading-relaxed text-cafe-noir/80">{t.audiences[k].short}</p>
          <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-kombu-green">
            {t.common.learnMore}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
          </div>
        </Link>
      ))}
    </div>
  );
};

export const ServiceRows = ({ keys = SERVICE_KEYS }) => {
  const { t, p } = useSite();
  return (
    <div className="border-t border-tan/60" data-testid="service-rows">
      {keys.map((k, i) => (
        <Link key={k} to={p(k)} data-testid={`service-row-${k}`} className="group grid gap-3 border-b border-tan/60 py-7 transition-colors md:grid-cols-12 md:items-baseline md:gap-8 md:py-9">
          <span className="font-serif text-sm text-moss-green md:col-span-1">0{i + 1}</span>
          <h3 className="font-serif text-2xl text-kombu-green transition-colors group-hover:text-moss-green md:col-span-5 md:text-[1.75rem]">{t.services[k].name}</h3>
          <p className="text-[15px] leading-relaxed text-cafe-noir/75 md:col-span-5">{t.services[k].short}</p>
          <ArrowUpRight className="hidden h-5 w-5 text-kombu-green transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 md:col-span-1 md:block md:justify-self-end" />
        </Link>
      ))}
    </div>
  );
};

export const ProcessSteps = ({ light = false }) => {
  const { t } = useSite();
  return (
    <ol className="grid gap-px md:grid-cols-5" data-testid="process-steps">
      {t.process.map((s, i) => (
        <li key={s.title} className={`border-t-2 pt-6 md:pr-6 ${light ? 'border-tan/60' : 'border-kombu-green'}`}>
          <span className={`font-serif text-3xl ${light ? 'text-tan' : 'text-moss-green'}`}>{i + 1}</span>
          <h3 className={`mt-3 text-base font-semibold ${light ? 'text-bone-light' : 'text-kombu-green'}`}>{s.title}</h3>
          <p className={`mt-2 text-sm leading-relaxed ${light ? 'text-bone/75' : 'text-cafe-noir/75'}`}>{s.text}</p>
        </li>
      ))}
    </ol>
  );
};

export const FounderCards = () => {
  const { t } = useSite();
  return (
    <div className="grid border-t border-tan md:grid-cols-2" data-testid="founder-cards">
      {t.founders.map((f, i) => (
        <article
          key={f.name}
          data-testid={`founder-${f.initials.toLowerCase()}`}
          className={`py-10 md:py-12 ${i === 0 ? 'md:border-r md:border-tan md:pr-12' : 'border-t border-tan md:border-t-0 md:pl-12'}`}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-moss-green">{f.role}</p>
          <h3 className="mt-4 font-serif text-[2.1rem] leading-tight text-kombu-green md:text-[2.5rem]">{f.name}</h3>
          <p className="mt-3 font-serif text-lg italic text-cafe-noir/80">{f.credential}</p>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-cafe-noir/85">{f.bio}</p>
          <a href={`mailto:${f.email}`} className="mt-6 inline-block break-all text-sm text-kombu-green underline-offset-4 hover:underline">{f.email}</a>
        </article>
      ))}
    </div>
  );
};

export const CtaBand = ({ title, text, secondaryTo, secondaryLabel, photo }) => {
  const { t, p } = useSite();
  return (
    <section className="relative overflow-hidden bg-kombu-green py-20 text-bone-light md:py-28" data-testid="cta-band">
      {photo ? (
        <div className="absolute inset-0"><Photo name={photo} sizes="100vw" /><div className="absolute inset-0 bg-kombu-green/[0.82]" /></div>
      ) : (
        <div className="engraved-lines pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-30 [mask-image:linear-gradient(to_left,black,transparent)]" />
      )}
      <Container className="relative">
        <SectionHeading title={title || t.home.final.title} intro={text || t.home.final.text} light />
        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
          <ButtonLink to={p('book')} variant="light" testId="cta-band-book">{t.common.bookCta}</ButtonLink>
          {secondaryTo && <ButtonLink to={secondaryTo} variant="ghostLight" testId="cta-band-secondary">{secondaryLabel}</ButtonLink>}
        </div>
        <p className="mt-10 font-serif text-lg italic text-tan">{t.brand.tagline}</p>
      </Container>
    </section>
  );
};

export const PageHero = ({ overline, title, intro, children, photo }) => (
  <section className="relative overflow-hidden border-b border-bone bg-bone-light">
    {!photo && <div className="engraved-lines pointer-events-none absolute inset-y-0 right-0 hidden w-2/5 opacity-60 [mask-image:radial-gradient(circle_at_70%_50%,black,transparent_70%)] md:block" />}
    <Container className={`relative py-16 md:py-24 ${photo ? 'grid gap-10 lg:grid-cols-12 lg:items-center' : ''}`}>
      <div className={photo ? 'lg:col-span-7' : ''}>
        <SectionHeading overline={overline} title={title} intro={intro} as="h1" />
        {children}
      </div>
      {photo && (
        <div className="aspect-[16/10] overflow-hidden lg:col-span-5 lg:aspect-[4/5] lg:max-h-[480px]" data-testid="page-hero-photo">
          <Photo name={photo} sizes="(min-width: 1024px) 40vw, 100vw" eager />
        </div>
      )}
    </Container>
  </section>
);

export { ArrowLink };
